import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ChatbotService } from '../../services/chatbot.service';
import { fixButtonAccents } from '../../constants/chatbot-buttons.constants';
import { MarkdownComponent } from 'ngx-markdown';
import { TranslateModule, TranslateService } from '@ngx-translate/core';


interface Message {
  message: string;
  sender: 'bot' | 'person';
}

const INITIAL_PUEBLOS = [
  'Corralejo',
  'Cotillo',
  'El Roque',
  'La Caldereta',
  'La Oliva',
  'Lajares',
  'Majanicho',
  'Parque Holandés',
  'Tindaya',
  'Vallebrón',
  'Villaverde'
];
@Component({
  selector: 'app-chatbot',
  standalone: true,
  imports: [CommonModule, FormsModule, MarkdownComponent, TranslateModule],
  templateUrl: './chatbot.component.html',
  styleUrl: './chatbot.component.css'
})
export class ChatbotComponent implements OnInit {
  private chatService = inject(ChatbotService);
  private router = inject(Router);
  private translateService = inject(TranslateService);

  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @ViewChild('chatInput') private chatInput!: ElementRef;

  isChatOpen = signal(false);
  userInput = signal('');
  messages = signal<Message[]>([]);

  ngOnInit(): void {
    this.setWelcomeMessage();
    this.translateService.onLangChange.subscribe(() => {
      this.messages.update(msgs => {
        const updated = [...msgs];
        updated[0] = { sender: 'bot', message: this.translateService.instant('CHATBOT.WELCOME') };
        return updated;
      });
    });
  }

  private setWelcomeMessage(): void {
    this.messages.set([{ sender: 'bot', message: this.translateService.instant('CHATBOT.WELCOME') }]);
  }

  botTexto = signal<string>('');
  botOpciones = signal<string[]>(INITIAL_PUEBLOS);
  freeSearchEnabled = signal(false);

  /** Indica si el usuario seleccionó un pueblo del listado inicial */
  hasSelectedPueblo = signal(false);

  openChat(): void {
    if (this.isChatOpen()) return;
    this.isChatOpen.set(true);
    setTimeout(() => {
      this.chatInput?.nativeElement?.focus();
      this.scrollToBottom();
    }, 100);
  }

  closeChat(): void {
    this.isChatOpen.set(false);
  }

  onUserInputChange(v: string) {
    this.userInput.set(v);
  }

  private readonly internalDomains = [
    '7maravillaslaoliva.com',
    window.location.hostname,
  ];

  onMessagesClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const anchor = target.closest('a');
    if (!anchor) return;

    const href = anchor.getAttribute('href');
    if (!href) return;

    // Intentar extraer path si es una URL de dominio interno
    try {
      const parsed = new URL(href, window.location.origin);
      const isInternal = this.internalDomains.some(
        d => parsed.hostname === d || parsed.hostname.endsWith('.' + d)
      );
      if (isInternal) {
        event.preventDefault();
        const path = parsed.pathname + parsed.search + parsed.hash;
        this.router.navigateByUrl(path);
        return;
      }
    } catch {
      // No es una URL válida, ignorar
    }

    // Links relativos (sin protocolo) también son internos
    if (!href.startsWith('http://') && !href.startsWith('https://')) {
      event.preventDefault();
      this.router.navigateByUrl(href);
      return;
    }

    // Links externos → abrir en nueva pestaña
    event.preventDefault();
    window.open(href, '_blank', 'noopener,noreferrer');
  }

  goBackToPueblos(): void {
    this.botOpciones.set(INITIAL_PUEBLOS);
    this.hasSelectedPueblo.set(false);
    this.freeSearchEnabled.set(false);
  }

  toggleFreeSearch(): void {
    this.freeSearchEnabled.update(v => !v);
    if (this.freeSearchEnabled()) {
      setTimeout(() => this.chatInput?.nativeElement?.focus(), 0);
    }
  }

  /** Reemplaza URLs sueltas por enlaces HTML con clase chat-link */
  private linkifyUrls(text: string): string {
    if (!text) return text;
    // Reemplaza URLs que NO estén ya dentro de una sintaxis markdown [texto](url)
    const label = this.translateService.instant('CHATBOT.LINK_LABEL');
    return text.replace(/(?<!\]\()https?:\/\/[^\s)]+/g, (url) => `<a class="chat-link" href="${url}">${label}</a>`);
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      try {
        const el = this.messagesContainer?.nativeElement;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      } catch (err) {
        console.error('Error al hacer scroll:', err);
      }
    }, 0);
  }

  sendMessage(): void {
    const text = this.userInput().trim();
    if (!text) return;

    // Marcar que el usuario ya interactuó (para mostrar botón de volver a pueblos)
    if (!this.hasSelectedPueblo()) {
      this.hasSelectedPueblo.set(true);
    }

    // Agregar mensaje del usuario
    this.messages.update((list) => [...list, { message: text, sender: 'person' }]);
    this.userInput.set('');
    this.botOpciones.set([]);
    this.freeSearchEnabled.set(false);
    this.scrollToBottom();

    // Crear un mensaje vacío del bot que iremos actualizando
    const botMessageIndex = this.messages().length;
    this.messages.update((list) => [...list, { message: '...', sender: 'bot' }]);

    // Suscribirse al stream unificado
    this.chatService.enviarConsultaStream(text).subscribe({
      next: (resultado) => {
        console.log('Chunk recibido:', resultado);
        // Actualizar el mensaje del bot progresivamente
        this.messages.update((list) => {
          const updated = [...list];
          updated[botMessageIndex] = {
            message: this.linkifyUrls(resultado.texto) || '...',
            sender: 'bot'
          };
          return updated;
        });

        // Si hay opciones, mostrarlas
        if (resultado.opciones && resultado.opciones.length > 0) {
          this.botOpciones.set(fixButtonAccents(resultado.opciones));
        }

        // Si la respuesta está completa (done: true), limpiar indicador de carga
        if (resultado.done) {
          console.log('Respuesta completa recibida');
        }

        this.scrollToBottom();
      },
      error: () => {
        this.messages.update((list) => {
          const updated = [...list];
          updated[botMessageIndex] = {
            message: this.translateService.instant('CHATBOT.ERROR'),
            sender: 'bot'
          };
          return updated;
        });
        this.botOpciones.set([]);
        this.scrollToBottom();
      },
      complete: () => {
        console.log('Stream completado');
        this.scrollToBottom();
      }
    });
  }



}
